import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import EmailInfraInspectModal from './EmailInfraInspectModal';
import GenericFeedInspectModal from './GenericFeedInspectModal';
import GhuntInspectModal from './GhuntInspectModal';
import HibpInspectModal from './HibpInspectModal';
import WhoisInspectModal from './WhoisInspectModal';
import LegalCourtsInspectModal from './LegalCourtsInspectModal';
import { InspectDrawerChromeProvider } from './InspectDrawerShell';
import { PIVOT_CTA_CLASS } from './investigation/TargetPivotsCard';
import { hasInfraInspectPayload } from '../utils/feedInspectUtils';
import {
  inspectCorporatePivotCopy,
  pickInspectCorporatePivot,
} from '../utils/inspectDrawerPivot';

function isWhoisIntelRow(row) {
  return String(row?.platform || '').toLowerCase() === 'whois intel';
}

function isLegalCourtsRow(row) {
  return row?.ui_bucket === 'legal_courts';
}

const FeedInspectContext = createContext(null);

/**
 * @returns {{ openInspect: Function, closeInspect: Function }}
 */
export function useFeedInspect() {
  const ctx = useContext(FeedInspectContext);
  if (!ctx) {
    throw new Error('useFeedInspect must be used within FeedInspectProvider');
  }
  return ctx;
}

function FeedInspectModalRouter({ state, onClose }) {
  if (!state?.row) return null;

  const { row, variant, email, status, ghuntMode, breaches } = state;

  if (variant === 'hibp') {
    return (
      <HibpInspectModal
        isOpen
        onClose={onClose}
        email={email || row.target || row.email}
        status={status || row.status}
        streamBreaches={breaches}
      />
    );
  }

  if (variant === 'ghunt') {
    return <GhuntInspectModal isOpen onClose={onClose} mode={ghuntMode || 'maps'} data={row} />;
  }

  if (hasInfraInspectPayload(row)) {
    return <EmailInfraInspectModal isOpen onClose={onClose} data={row} />;
  }

  if (variant === 'legal_courts' || isLegalCourtsRow(row)) {
    return <LegalCourtsInspectModal isOpen onClose={onClose} data={row} />;
  }

  if (isWhoisIntelRow(row)) {
    return <WhoisInspectModal isOpen onClose={onClose} data={row} />;
  }

  return <GenericFeedInspectModal isOpen onClose={onClose} data={row} />;
}

/**
 * Global inspect modal host for all investigation feeds.
 *
 * @param {{
 *   children: React.ReactNode,
 *   discoveryPivots?: object[],
 *   onPivotLead?: (pivot: object) => void,
 * }} props
 */
export function FeedInspectProvider({
  children,
  discoveryPivots = [],
  onPivotLead = null,
}) {
  const [state, setState] = useState(null);

  const openInspect = useCallback((row, options = {}) => {
    if (!row) return;
    setState({ row, ...options });
  }, []);

  const closeInspect = useCallback(() => setState(null), []);

  const headerActions = useMemo(() => {
    if (!state?.row || typeof onPivotLead !== 'function') return null;
    const pivot = pickInspectCorporatePivot(state.row, discoveryPivots);
    if (!pivot) return null;
    const copy = inspectCorporatePivotCopy(pivot);
    return (
      <button
        type="button"
        className={`${PIVOT_CTA_CLASS} min-h-[36px] px-3`}
        title={copy.title}
        aria-label={copy.title}
        data-testid="inspect-drawer-pivot-corporate"
        onClick={() => {
          onPivotLead(pivot);
          closeInspect();
        }}
      >
        ↗ {copy.label}
      </button>
    );
  }, [state, discoveryPivots, onPivotLead, closeInspect]);

  const value = useMemo(() => ({ openInspect, closeInspect }), [openInspect, closeInspect]);

  return (
    <FeedInspectContext.Provider value={value}>
      <InspectDrawerChromeProvider headerActions={headerActions}>
        {children}
        <FeedInspectModalRouter state={state} onClose={closeInspect} />
      </InspectDrawerChromeProvider>
    </FeedInspectContext.Provider>
  );
}

export default FeedInspectProvider;
